import { memo, useEffect, useState, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { usePlaceBet } from '@/hooks/usePlaceBet'
import { usePlaceSell } from '@/hooks/usePlaceSell'
import { useCancelOrder } from '@/hooks/useCancelOrder'
import { useClobClientStatus } from '@/hooks/useClobClient'
import { useOpenOrders } from '@/hooks/useOpenOrders'
import { usePositions } from '@/hooks/usePositions'
import { useUsdcBalance } from '@/hooks/useUsdcBalance'
import { usePmWalletStore } from '@/store/pmWalletStore'
import { fetchBestBidAsk } from '@/lib/polymarketApi'
import type { MatrixCell } from '@/lib/rangeMarketUtils'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type PriceTab = 'bid' | 'mid' | 'ask'

interface OrderPlacementDialogProps {
  cell: MatrixCell | null
  initialSide?: 'YES' | 'NO'
  onClose: () => void
  onPlaced?: () => void
}

export const OrderPlacementDialog = memo(function OrderPlacementDialog({
  cell,
  initialSide = 'YES',
  onClose,
  onPlaced,
}: OrderPlacementDialogProps) {
  const queryClient = useQueryClient()
  const placeBet = usePlaceBet()
  const { status: clobStatus } = useClobClientStatus()
  const clobReady = clobStatus === 'ready'
  const clobFailed = clobStatus === 'failed'
  const clobLoading = clobStatus === 'loading' || clobStatus === 'idle'
  const placeSell = usePlaceSell()
  const cancelOrder = useCancelOrder()
  const ordersMap = useOpenOrders()
  const activeWallet = usePmWalletStore((s) => s.activeWallet)
  const pmProxyAddress = usePmWalletStore((s) => s.pmProxyAddress)
  const activeAddress = activeWallet === 'pm' ? pmProxyAddress : null
  const positionsMap = usePositions(activeAddress ?? undefined)
  const { balance, refetch: refetchBalance } = useUsdcBalance()
  const [side, setSide] = useState<'YES' | 'NO'>(initialSide)
  const [activeTab, setActiveTab] = useState<PriceTab>('mid')
  const [amountUsd, setAmountUsd] = useState(5)
  const [quantityInput, setQuantityInput] = useState('5')
  /** Cents as editable string (e.g. "50.5"); only digits, one dot, one decimal. */
  const [priceInput, setPriceInput] = useState('')
  const userEditedPriceRef = useRef(false)
  const [bid, setBid] = useState<number | null>(null)
  const [ask, setAsk] = useState<number | null>(null)
  const [mid, setMid] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const lockedTokenId = useRef<string | null>(null)

  useEffect(() => {
    if (cell) setSide(initialSide)
  }, [cell, initialSide])

  const tokenId = cell ? (side === 'YES' ? cell.yesTokenId : cell.noTokenId) : ''
  const positionInfo = tokenId ? positionsMap.get(tokenId) : null
  const orderInfoRaw = tokenId ? ordersMap.get(tokenId) : null
  const orderSizeRemaining = orderInfoRaw
    ? Math.max(0, orderInfoRaw.sizeTotal - orderInfoRaw.sizeMatched)
    : 0
  const orderInfo = orderInfoRaw && orderSizeRemaining > 0 ? orderInfoRaw : null
  const fallbackPrice = cell ? (side === 'YES' ? cell.yesPrice : cell.noPrice) ?? 0.5 : 0.5
  const hasOppositePosition = cell
    ? (side === 'YES' ? !!positionsMap.get(cell.noTokenId) : !!positionsMap.get(cell.yesTokenId))
    : false
  const hasOppositeOrder = cell
    ? (side === 'YES' ? !!ordersMap.get(cell.noTokenId) : !!ordersMap.get(cell.yesTokenId))
    : false
  const tabPrice = activeTab === 'bid' ? (bid ?? fallbackPrice)
    : activeTab === 'ask' ? (ask ?? fallbackPrice)
    : (mid ?? fallbackPrice)
  const parsedCustom = priceInput === '' ? null : (() => {
    const n = parseFloat(priceInput)
    if (Number.isNaN(n) || n < 0 || n > 99.9) return null
    return Math.round(n * 10) / 1000
  })()
  const selectedPrice = parsedCustom ?? tabPrice

  const formatCents = (p: number) => `${(p * 100).toFixed(1)}`

  const handlePriceInputChange = useCallback((raw: string) => {
    userEditedPriceRef.current = true
    // Принимаем и точку, и запятую (RU-раскладка) как десятичный разделитель.
    // Потом фильтруем только цифры + точку.
    const normalized = raw.replace(/,/g, '.')
    const filtered = normalized.replace(/[^\d.]/g, '')
    const dotCount = (filtered.match(/\./g) ?? []).length
    let s = dotCount > 1 ? filtered.slice(0, filtered.indexOf('.') + 1) + filtered.slice(filtered.indexOf('.') + 1).replace(/\./g, '') : filtered
    if (s.includes('.')) {
      const [head, tail] = s.split('.')
      s = head + '.' + (tail.slice(0, 1))
    }
    if (s.startsWith('.') && s.length > 2) s = s.slice(0, 2)
    if (s.length > 5) s = s.slice(0, 5)
    setPriceInput(s)
  }, [])

  const refreshPrices = useCallback(() => {
    const tokenId = lockedTokenId.current
    if (!tokenId) return
    setLoading(true)
    fetchBestBidAsk(tokenId)
      .then(({ bid: bestBid, ask: bestAsk, mid: bestMid }) => {
        if (tokenId !== lockedTokenId.current) return
        setBid(bestBid)
        setAsk(bestAsk)
        setMid(bestMid ?? (bestBid + bestAsk) / 2)
      })
      .catch((_err) => {
        if (tokenId !== lockedTokenId.current) return
        toast.error('Цены временно недоступны, показаны приближённые')
        const fb = fallbackPrice
        setBid(fb)
        setAsk(fb)
        setMid(fb)
      })
      .finally(() => {
        if (tokenId === lockedTokenId.current) setLoading(false)
      })
  }, [fallbackPrice])

  useEffect(() => {
    if (cell) setAmountUsd(5)
  }, [cell])

  useEffect(() => {
    if (!tokenId) {
      lockedTokenId.current = null
      return
    }
    if (lockedTokenId.current === tokenId) return
    lockedTokenId.current = tokenId
    userEditedPriceRef.current = false
    setActiveTab('mid')
    setPriceInput('')
    setBid(null)
    setAsk(null)
    setMid(null)
    refreshPrices()
  }, [tokenId, refreshPrices])

  useEffect(() => {
    if (!tokenId) return
    const tick = () => {
      refreshPrices()
      void refetchBalance()
      void queryClient.invalidateQueries({ queryKey: ['positions'] })
      void queryClient.invalidateQueries({ queryKey: ['portfolioValue'] })
      void queryClient.invalidateQueries({ queryKey: ['openOrders'] })
    }
    const t = setInterval(tick, 5_000)
    return () => clearInterval(t)
  }, [tokenId, refreshPrices, queryClient, refetchBalance])

  useEffect(() => {
    if (userEditedPriceRef.current) return
    setPriceInput((tabPrice * 100).toFixed(1))
  }, [tabPrice])

  const handleTabClick = useCallback((tab: PriceTab) => {
    setActiveTab(tab)
    userEditedPriceRef.current = false
    const v = tab === 'bid' ? (bid ?? fallbackPrice) : tab === 'ask' ? (ask ?? fallbackPrice) : (mid ?? fallbackPrice)
    setPriceInput(formatCents(v))
    refreshPrices()
  }, [bid, ask, mid, fallbackPrice, refreshPrices])

  const adjustAmount = useCallback((delta: number) => {
    setAmountUsd((prev) => Math.max(5, Math.min(10000, prev + delta)))
  }, [])

  useEffect(() => {
    setQuantityInput(String(amountUsd))
  }, [amountUsd])

  const availableToBuy = selectedPrice > 0 && balance != null ? Math.floor(balance / selectedPrice) : null
  const availableToSell = positionInfo ? Math.round(positionInfo.size) : 0

  const effectiveQuantity = (() => {
    const n = parseInt(quantityInput, 10)
    if (quantityInput === '' || Number.isNaN(n)) return amountUsd
    return Math.max(5, Math.min(10000, n))
  })()

  const setQuantityToAvailableBuy = useCallback(() => {
    if (availableToBuy != null) {
      const v = Math.max(5, Math.min(10000, availableToBuy))
      setAmountUsd(v)
      setQuantityInput(String(v))
    }
  }, [availableToBuy])

  const setQuantityToAvailableSell = useCallback(() => {
    const v = Math.max(0, availableToSell)
    setAmountUsd(v)
    setQuantityInput(String(v))
  }, [availableToSell])

  const handleQuantityInputChange = useCallback((raw: string) => {
    const digits = raw.replace(/\D/g, '')
    const truncated = digits.slice(0, 5)
    setQuantityInput(truncated)
  }, [])

  const handleQuantityBlur = useCallback(() => {
    const n = parseInt(quantityInput, 10)
    if (quantityInput === '' || Number.isNaN(n) || n < 5) {
      setAmountUsd(5)
      setQuantityInput('5')
    } else if (n > 10000) {
      setAmountUsd(10000)
      setQuantityInput('10000')
    } else {
      setAmountUsd(n)
      setQuantityInput(String(n))
    }
  }, [quantityInput])

  const handlePlace = useCallback(() => {
    if (!cell || placeBet.isPending) return
    if (effectiveQuantity < 5) {
      toast.error('Минимальное количество 5')
      return
    }
    placeBet.mutate(
      {
        tokenId,
        side,
        amountUsd: effectiveQuantity,
        tickSize: cell.tickSize,
        negRisk: cell.negRisk,
        yesPrice: side === 'YES' ? selectedPrice : cell.yesPrice,
        noPrice: side === 'NO' ? selectedPrice : cell.noPrice,
        useExactPrice: true,
      },
      {
        onSuccess: () => {
          onPlaced?.()
          onClose()
        },
      }
    )
  }, [cell, tokenId, side, effectiveQuantity, selectedPrice, placeBet, onClose, onPlaced])

  const handleSell = useCallback(() => {
    if (!cell || placeSell.isPending) return
    if (effectiveQuantity < 5 || effectiveQuantity > availableToSell) return
    placeSell.mutate(
      {
        tokenId,
        size: effectiveQuantity,
        price: selectedPrice,
        tickSize: cell.tickSize,
        negRisk: cell.negRisk,
      },
      {
        onSuccess: () => {
          onPlaced?.()
          onClose()
        },
      }
    )
  }, [cell, tokenId, effectiveQuantity, selectedPrice, availableToSell, placeSell, onClose, onPlaced])

  const handleCancel = useCallback(() => {
    if (!tokenId || cancelOrder.isPending) return
    cancelOrder.mutate(tokenId, {
      onSuccess: () => {
        onPlaced?.()
        onClose()
      },
    })
  }, [tokenId, cancelOrder, onClose, onPlaced])

  if (!cell) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-dialog-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Закрыть"
      />
      <div className="relative z-10 w-full max-w-sm rounded-xl border border-[#374151] bg-[#111827] p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 id="order-dialog-title" className="text-lg font-semibold text-white">
            Размещение ордера
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[#94a3b8] hover:bg-white/10 hover:text-white"
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        {/* Side tabs: ДА | НЕТ */}
        <div className="mb-2 flex gap-2">
          {(['YES', 'NO'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSide(s)}
              className={cn(
                'flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                side === s
                  ? s === 'YES'
                    ? 'bg-[#00ff6a] text-black'
                    : 'bg-red-500 text-white'
                  : 'bg-[#1f2937] text-[#94a3b8] hover:bg-[#374151] hover:text-white'
              )}
            >
              {s === 'YES' ? 'ДА' : 'НЕТ'}
            </button>
          ))}
        </div>

        {/* Price tabs: Бид | Мид | Аск */}
        <div className="mb-2 flex gap-2">
          {(['bid', 'mid', 'ask'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => handleTabClick(tab)}
              className={cn(
                'flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                activeTab === tab
                  ? 'bg-[#00ff6a] text-black'
                  : 'bg-[#1f2937] text-[#94a3b8] hover:bg-[#374151] hover:text-white'
              )}
            >
              {tab === 'bid' ? 'Бид' : tab === 'mid' ? 'Мид' : 'Аск'}
            </button>
          ))}
        </div>
        <div className="mb-4 flex gap-2 text-xs text-[#94a3b8]">
          <div className="flex-1 text-center">
            {loading ? '…' : bid != null ? formatCents(bid) : '—'}
          </div>
          <div className="flex-1 text-center">
            {loading ? '…' : mid != null ? formatCents(mid) : '—'}
          </div>
          <div className="flex-1 text-center">
            {loading ? '…' : ask != null ? formatCents(ask) : '—'}
          </div>
        </div>

        {/* Price */}
        <div className="mb-4">
          <div className="mb-1 text-xs text-[#94a3b8]">Цена</div>
          <div className="flex items-center rounded-lg bg-[#1f2937] px-4 py-3 text-xl font-bold text-[#00ff6a]">
            <input
              type="text"
              inputMode="decimal"
              value={loading ? '' : priceInput}
              onChange={(e) => handlePriceInputChange(e.target.value)}
              className="min-w-0 flex-1 bg-transparent outline-none"
              aria-label="Цена в центах"
            />
            <span className="shrink-0" aria-hidden>¢</span>
          </div>
        </div>

        {/* Amount */}
        <div className="mb-4">
          <div className="mb-2 text-xs text-[#94a3b8]">Количество (мин. 5)</div>
          <div className="mb-2 rounded-lg bg-[#1f2937] px-4 py-3 text-lg font-semibold text-white">
            <input
              type="text"
              inputMode="numeric"
              value={quantityInput}
              onChange={(e) => handleQuantityInputChange(e.target.value)}
              onBlur={handleQuantityBlur}
              className="w-full min-w-0 bg-transparent outline-none tabular-nums"
              aria-label="Количество контрактов"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[-100, -10, -1].map((d) => (
              <Button
                key={d}
                variant="outline"
                size="sm"
                onClick={() => adjustAmount(d)}
                className="min-w-0 py-1 px-2 border-[#374151] bg-[#1f2937] text-white hover:bg-[#374151]"
              >
                {d}
              </Button>
            ))}
            {[1, 10, 100].map((d) => (
              <Button
                key={d}
                variant="outline"
                size="sm"
                onClick={() => adjustAmount(d)}
                className="min-w-0 py-1 px-2 border-[#00ff6a]/50 bg-[#1f2937] text-[#00ff6a] hover:bg-[#00ff6a]/20"
              >
                +{d}
              </Button>
            ))}
          </div>
        </div>

        {/* Current position and availability — one line, same format */}
        <div className="mb-4 rounded-lg bg-[#1f2937] px-4 py-3">
          <div className="text-xs text-[#94a3b8]">
            Тек. поз. {positionInfo ? (
              <span className="text-white">{Math.round(positionInfo.size)} шт. по {(positionInfo.avgPrice * 100).toFixed(1)}¢</span>
            ) : (
              <span className="text-[#64748b]">Нет</span>
            )}
            {' · '}
            Размер ордера <span className="text-white">{selectedPrice > 0 ? (Math.round(effectiveQuantity * selectedPrice * 100) / 100) : 0} USDC</span>
            {' · '}
            Доступно купить{' '}
            <button
              type="button"
              onClick={setQuantityToAvailableBuy}
              disabled={availableToBuy == null}
              className={cn(
                'inline-flex items-center rounded px-2 py-0.5 text-xs font-medium text-white',
                'border border-[#4b5563] bg-[#374151] shadow-[0_1px_0_0_#4b5563] active:shadow-none active:translate-y-px',
                'hover:bg-[#4b5563] disabled:opacity-50 disabled:cursor-default disabled:hover:bg-[#374151]',
                availableToBuy == null && 'cursor-default'
              )}
            >
              {availableToBuy != null ? availableToBuy : '—'}
            </button>
            {' · '}
            Доступно продать{' '}
            <button
              type="button"
              onClick={setQuantityToAvailableSell}
              className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium text-white border border-[#4b5563] bg-[#374151] shadow-[0_1px_0_0_#4b5563] active:shadow-none active:translate-y-px hover:bg-[#4b5563]"
            >
              {availableToSell}
            </button>
          </div>
        </div>

        <div className="flex gap-2">
          {orderInfo ? (
            <Button
              type="button"
              onClick={handleCancel}
              disabled={cancelOrder.isPending}
              className="flex-1 bg-[#cd7f32] text-white hover:bg-[#b87333] disabled:opacity-50"
            >
              {cancelOrder.isPending ? 'Отмена...' : `Отменить ордер ${(orderInfo.orderPrice * 100).toFixed(1)}¢ × ${Math.round(orderSizeRemaining)}`}
            </Button>
          ) : (
            <Button
              onClick={handlePlace}
              disabled={
                placeBet.isPending ||
                effectiveQuantity < 5 ||
                (availableToBuy != null && effectiveQuantity > availableToBuy) ||
                hasOppositePosition ||
                hasOppositeOrder ||
                !clobReady
              }
              title={
                !clobReady
                  ? clobFailed
                    ? 'Polymarket CLOB-аккаунт не инициализирован. Сделайте первый ордер на polymarket.com через тот же PM-кошелёк, чтобы активировать CLOB.'
                    : 'Подключаемся к Polymarket CLOB…'
                  : hasOppositePosition
                    ? 'Закройте противоположную позицию'
                    : hasOppositeOrder
                      ? 'Сначала отмените ордер по противоположной стороне'
                      : undefined
              }
              className={cn(
                'flex-1 bg-[#00ff6a] text-black hover:bg-[#00ff6a]/90',
                (hasOppositePosition || hasOppositeOrder || !clobReady) && 'opacity-60 cursor-not-allowed'
              )}
            >
              {placeBet.isPending
                ? 'Покупка...'
                : clobLoading
                  ? 'Загрузка CLOB…'
                  : clobFailed
                    ? 'CLOB недоступен'
                    : 'Купить'}
            </Button>
          )}
          <Button
            type="button"
            onClick={handleSell}
            disabled={placeSell.isPending || effectiveQuantity < 5 || effectiveQuantity > availableToSell || !clobReady}
            title={!clobReady && clobFailed ? 'CLOB недоступен — см. кнопку «Купить»' : undefined}
            className="flex-1 bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 disabled:pointer-events-none"
          >
            {placeSell.isPending ? 'Размещение...' : 'Продать'}
          </Button>
        </div>
        {clobFailed && (
          <div className="mt-3 rounded-lg border border-[#f59e0b]/40 bg-[#f59e0b]/10 px-3 py-2 text-[11px] leading-relaxed text-[#f59e0b]">
            <div className="mb-1 font-semibold">CLOB-аккаунт не инициализирован</div>
            Для этого EOA Polymarket CLOB не выдаёт API-ключ. Сделайте первый
            ордер напрямую на{' '}
            <a
              href="https://polymarket.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[#fbbf24]"
            >
              polymarket.com
            </a>
            {' '}через тот же PM-кошелёк — после этого CLOB активируется,
            и PolyStrike Visual сможет размещать ордера.
          </div>
        )}
      </div>
    </div>
  )
})
